import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

interface DatePickerProps {
  value: string; // 'YYYY-MM-DD' or ''
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  ariaLabel: string;
  placeholder?: string;
}

const pad = (n: number) => String(n).padStart(2, '0');
const toIso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseIso = (s: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};
// Monday-first weekday index (0 = Monday … 6 = Sunday)
const mondayIndex = (d: Date) => (d.getDay() + 6) % 7;

export function DatePicker({
  value,
  onChange,
  min,
  max,
  ariaLabel,
  placeholder,
}: DatePickerProps) {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => parseIso(value), [value]);
  const minDate = useMemo(() => (min ? parseIso(min) : null), [min]);
  const maxDate = useMemo(() => (max ? parseIso(max) : null), [max]);
  const [view, setView] = useState<Date>(() => selected ?? new Date());
  const rootRef = useRef<HTMLDivElement>(null);
  const gridId = useId();

  useEffect(() => {
    if (open) setView(selected ?? new Date());
  }, [open, selected]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const dayNames = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(lang, { weekday: 'short' });
    // 2024-01-01 is a Monday
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 0, 1 + i)));
  }, [lang]);

  const monthLabel = useMemo(
    () => new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric' }).format(view),
    [lang, view],
  );

  const buttonLabel = useMemo(
    () =>
      selected
        ? new Intl.DateTimeFormat(lang, { day: '2-digit', month: 'short', year: 'numeric' }).format(
            selected,
          )
        : '',
    [lang, selected],
  );

  const startOfMonth = new Date(view.getFullYear(), view.getMonth(), 1);
  const leadingBlanks = mondayIndex(startOfMonth);
  const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();

  const isDisabled = (d: Date) => {
    if (minDate && d < minDate) return true;
    if (maxDate && d > maxDate) return true;
    return false;
  };
  const sameDay = (a: Date | null, b: Date) =>
    !!a && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const today = new Date();

  const goMonth = (delta: number) =>
    setView((v) => new Date(v.getFullYear(), v.getMonth() + delta, 1));

  return (
    <div className="pl-datepicker" ref={rootRef}>
      <button
        type="button"
        className={`pl-date-btn ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Calendar size={14} aria-hidden="true" />
        <span className={buttonLabel ? '' : 'pl-date-btn__placeholder'}>
          {buttonLabel || placeholder || ariaLabel}
        </span>
      </button>
      {open && (
        <div className="pl-cal" role="dialog" aria-label={ariaLabel}>
          <div className="pl-cal__head">
            <button
              type="button"
              className="pl-cal__nav"
              onClick={() => goMonth(-1)}
              aria-label="←"
            >
              <ChevronLeft size={16} aria-hidden="true" />
            </button>
            <span className="pl-cal__month">{monthLabel}</span>
            <button
              type="button"
              className="pl-cal__nav"
              onClick={() => goMonth(1)}
              aria-label="→"
            >
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
          <div className="pl-cal__grid" role="grid" aria-labelledby={gridId}>
            {dayNames.map((d, i) => (
              <span key={`h-${i}`} className="pl-cal__dow" role="columnheader">
                {d}
              </span>
            ))}
            {Array.from({ length: leadingBlanks }).map((_, i) => (
              <span key={`b-${i}`} className="pl-cal__blank" aria-hidden="true" />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const d = new Date(view.getFullYear(), view.getMonth(), i + 1);
              const disabled = isDisabled(d);
              const isSel = sameDay(selected, d);
              const isToday = sameDay(today, d);
              return (
                <button
                  key={`d-${i}`}
                  type="button"
                  className={`pl-cal__day${isSel ? ' is-selected' : ''}${
                    isToday && !isSel ? ' is-today' : ''
                  }`}
                  disabled={disabled}
                  aria-pressed={isSel}
                  onClick={() => {
                    onChange(toIso(d));
                    setOpen(false);
                  }}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
